import { Link } from "react-router-dom";
import type { PackDto } from "@gaole/shared";
import { rm } from "../../lib/format";
import { TierBadge } from "./TierBadge";
import { PackVisual } from "../fx/PackVisual";
import { HoloCard } from "../fx/HoloCard";
import { ListingTitle } from "../ListingTitle";

type PackCardPack = Pick<
  PackDto,
  | "id"
  | "name"
  | "images"
  | "price"
  | "compareAtPrice"
  | "totalRemaining"
  | "topTier"
  | "topTierColor"
  | "foilLabel"
  | "foilColor"
>;

/**
 * A sealed pack on a shelf. Shows the foil wrapper rather than the product
 * photo so the "this is still closed" promise reads at a glance.
 */
export function PackCard({ pack, className = "" }: { pack: PackCardPack; className?: string }) {
  return (
    <Link
      to={`/packs/${pack.id}`}
      className={`group neon-ring block min-w-0 overflow-hidden rounded-2xl bg-surface transition-transform active:scale-[0.98] lg:hover:-translate-y-1 lg:hover:glow-brand ${className}`}
    >
      <div className="relative flex justify-center bg-gradient-to-b from-white/[0.06] to-transparent p-4">
        <HoloCard holo={0.26} max={9} lift={10} scale={1.04}>
          <PackVisual
            id={pack.id}
            name={pack.name}
            images={pack.images}
            foilLabel={pack.foilLabel}
            foilColor={pack.foilColor}
            sweep
            className="h-[168px] w-[118px]"
          />
        </HoloCard>
      </div>

      <div className="min-w-0 space-y-1.5 border-t border-white/5 p-3">
        <ListingTitle className="font-semibold text-white">{pack.name}</ListingTitle>
        <div className="flex items-center justify-between gap-2">
          <span className="font-display text-sm font-bold text-brand-pink">{rm(pack.price)}</span>
          <span className="text-[10px] text-white/40">{pack.totalRemaining} left</span>
        </div>
        {pack.topTier ? (
          <TierBadge name={pack.topTier} color={pack.topTierColor ?? undefined} glow size="sm" />
        ) : null}
      </div>
    </Link>
  );
}
